(() => {
  'use strict';

  if (window.GlamoreQuickView) {
    window.GlamoreQuickView.init(document);
    return;
  }

  const controllers = new WeakMap();
  const focusableSelector = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class GlamoreQuickViewController {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-qv-modal]');
      this.dialog = root.querySelector('[data-qv-dialog]');
      this.closeButton = root.querySelector('[data-qv-close]');
      this.previousButton = root.querySelector('[data-qv-previous]');
      this.nextButton = root.querySelector('[data-qv-next]');
      this.openButtons = [...root.querySelectorAll('[data-qv-open]')];
      this.products = this.readProducts();
      this.currentIndex = 0;
      this.lastTrigger = null;
      this.closeTimer = null;
      this.abortController = new AbortController();

      if (!this.modal || !this.dialog || this.products.length === 0) return;

      this.bindEvents();
      this.updatePaginationState();
    }

    readProducts() {
      return [...this.root.querySelectorAll('[data-qv-product]')]
        .map((node) => {
          try {
            return JSON.parse(node.textContent);
          } catch (error) {
            console.warn('GLAMORE Quick View: invalid product data.', error);
            return null;
          }
        })
        .filter(Boolean);
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.openButtons.forEach((button, index) => {
        button.addEventListener('click', () => this.open(index, button), { signal });
      });

      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.previousButton?.addEventListener('click', () => this.showRelative(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.showRelative(1), { signal });

      this.modal.addEventListener(
        'click',
        (event) => {
          if (event.target === this.modal) this.close();
        },
        { signal }
      );

      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    }

    open(index, trigger) {
      window.clearTimeout(this.closeTimer);
      this.currentIndex = this.clampIndex(index);
      this.lastTrigger = trigger || document.activeElement;
      this.render(this.products[this.currentIndex]);
      this.modal.hidden = false;
      document.documentElement.classList.add('glamore-qv-open');

      window.requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    close({ restoreFocus = true } = {}) {
      if (this.modal.hidden) return;

      this.modal.classList.remove('is-open');
      document.documentElement.classList.remove('glamore-qv-open');

      const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180;
      this.closeTimer = window.setTimeout(() => {
        this.modal.hidden = true;
        if (restoreFocus && this.lastTrigger instanceof HTMLElement) {
          this.lastTrigger.focus({ preventScroll: true });
        }
      }, delay);
    }

    showRelative(step) {
      this.currentIndex = this.clampIndex(this.currentIndex + step);
      this.render(this.products[this.currentIndex]);
      this.dialog.scrollTop = 0;
      this.root.querySelector('[data-qv-modal-content]')?.scrollTo(0, 0);
    }

    clampIndex(index) {
      const count = this.products.length;
      return ((index % count) + count) % count;
    }

    render(product) {
      this.setText('[data-qv-modal-category]', product.category || product.vendor || '');
      this.setText('[data-qv-modal-title]', product.title || '');
      this.setText('[data-qv-modal-price]', product.price || '');
      this.setText('[data-qv-modal-price-note]', product.priceNote || '');

      const image = this.root.querySelector('[data-qv-modal-image]');
      if (image) {
        image.src = product.image || '';
        image.alt = product.imageAlt || product.title || '';
      }

      const description = this.root.querySelector('[data-qv-modal-description]');
      if (description) {
        description.innerHTML = product.descriptionHtml || this.root.dataset.emptyDescription || '';
      }

      const details = this.normalizeList(product.lookDetails, product.tags);
      const detailsList = this.root.querySelector('[data-qv-modal-details]');
      const detailsGroup = this.root.querySelector('[data-qv-details-group]');
      if (detailsList) {
        detailsList.replaceChildren(...details.map((detail) => {
          const item = document.createElement('li');
          item.textContent = detail;
          return item;
        }));
      }
      if (detailsGroup) detailsGroup.hidden = details.length === 0;

      const styleWith = this.toText(product.styleWith) || this.root.dataset.defaultStyleWith || '';
      this.setText('[data-qv-modal-style]', styleWith);
      const styleGroup = this.root.querySelector('[data-qv-style-group]');
      if (styleGroup) styleGroup.hidden = !styleWith;

      this.updateActions(product);
      this.updatePaginationState();
    }

    normalizeList(value, fallback = []) {
      let source = value;
      if (source && typeof source === 'object' && !Array.isArray(source)) {
        source = source.value ?? source.items ?? [];
      }
      if (!Array.isArray(source)) {
        source = typeof source === 'string' ? source.split(/[|\n]/) : [];
      }
      const cleaned = source.map((item) => this.toText(item)).filter(Boolean);
      if (cleaned.length > 0) return cleaned;

      const fallbackArray = Array.isArray(fallback) ? fallback : [];
      return fallbackArray.map((item) => this.toText(item)).filter(Boolean);
    }

    toText(value) {
      if (value == null) return '';
      if (typeof value === 'object') return String(value.value ?? value.label ?? '').trim();
      return String(value).trim();
    }

    updateActions(product) {
      const requestButton = this.root.querySelector('[data-qv-request]');
      const smsButton = this.root.querySelector('[data-qv-sms]');
      const productUrl = new URL(product.url || '/', window.location.origin).href;
      const subject = `Product inquiry: ${product.title || 'GLAMORE look'}`;
      const message = `I would like more information about ${product.title || 'this GLAMORE look'}. ${productUrl}`;
      const email = (this.root.dataset.inquiryEmail || '').trim();

      if (requestButton) {
        if (email) {
          requestButton.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
        } else {
          const requestPath = (this.root.dataset.requestPath || '/pages/contact-us').trim();
          const requestUrl = new URL(requestPath || '/pages/contact-us', window.location.origin);
          requestUrl.searchParams.set('product', product.title || 'GLAMORE look');
          requestButton.href = requestUrl.href;
        }
      }

      if (smsButton) {
        const phone = (this.root.dataset.smsPhone || '').trim();
        const normalizedPhone = phone.replace(/(?!^\+)\D/g, '');
        smsButton.hidden = !normalizedPhone;
        if (normalizedPhone) {
          smsButton.href = `sms:${normalizedPhone}?&body=${encodeURIComponent(message)}`;
        }
      }
    }

    updatePaginationState() {
      const disabled = this.products.length <= 1;
      if (this.previousButton) this.previousButton.disabled = disabled;
      if (this.nextButton) this.nextButton.disabled = disabled;
    }

    setText(selector, value) {
      const element = this.root.querySelector(selector);
      if (element) element.textContent = value || '';
    }

    handleKeydown(event) {
      if (this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showRelative(-1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showRelative(1);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...this.dialog.querySelectorAll(focusableSelector)]
        .filter((element) => element.offsetParent !== null && !element.hidden);
      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    destroy() {
      window.clearTimeout(this.closeTimer);
      this.close({ restoreFocus: false });
      this.abortController.abort();
      controllers.delete(this.root);
    }
  }

  function init(scope = document) {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-glamore-qv]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-glamore-qv]'));

    roots.forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new GlamoreQuickViewController(root));
    });
  }

  function unload(scope) {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-glamore-qv]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-glamore-qv]'));
    roots.forEach((root) => controllers.get(root)?.destroy());
  }

  window.GlamoreQuickView = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
